import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Bell, BellOff, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function PushNotificationToggle() {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if push notifications are supported
    const checkSupport = () => {
      setIsSupported('serviceWorker' in navigator && 'PushManager' in window);
    };
    checkSupport();

    if (!user) {
      setLoading(false);
      return;
    }

    // Check current subscription status
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/notifications/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        setIsSubscribed(res.data.subscribed);
      } catch (error) {
        console.error('Error checking notification status:', error);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [user]);

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser');
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please sign in first');
      return;
    }

    setLoading(true);

    try {
      // Request permission
      const hasPermission = await requestPermission();
      if (!hasPermission) {
        toast.error('Notification permission denied');
        setLoading(false);
        return;
      }

      // Register service worker if not already
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          // This is a placeholder VAPID key - in production, generate real keys
          'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
        )
      });

      // Send subscription to backend
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth'))
        }
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      setIsSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed to enable notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notifications/unsubscribe`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      // Also unsubscribe from push manager
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
      disabled={loading || !user}
      className={`flex items-center gap-2 ${isSubscribed ? 'text-cyan-400' : 'text-white/60'}`}
      data-testid="push-notification-toggle"
    >
      {isSubscribed ? (
        <>
          <Bell className="w-5 h-5" />
          <span className="hidden md:inline">Notifications On</span>
        </>
      ) : (
        <>
          <BellOff className="w-5 h-5" />
          <span className="hidden md:inline">Enable Notifications</span>
        </>
      )}
    </Button>
  );
}

// Helper functions
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
